import React from 'react';
import { useNavigate } from 'react-router-dom';
import { FaCrown, FaArrowRight } from 'react-icons/fa';

const UpgradePrompt = ({ feature, currentPlan = 'starter' }) => {
  const navigate = useNavigate();

  const proFeatures = {
    'profit-tracker': {
      title: 'Profit Tracker',
      description: 'Analyze your profits, costs, and margins with detailed reports and trends.',
      benefits: [
        'Revenue and profit analysis',
        'Cost breakdown by product',
        'Margin and ROI tracking',
        'Period comparison',
        'Export detailed reports'
      ]
    },
    'predictive-alerts': {
      title: 'Predictive Alerts',
      description: 'Get intelligent alerts about stock levels, price changes, and performance issues.',
      benefits: [
        'Low stock predictions',
        'Price optimization alerts',
        'Competition monitoring',
        'Performance warnings',
        'Email notifications'
      ]
    },
    'money-recovery': {
      title: 'Money Recovery',
      description: 'Automatically detect and claim reimbursements from Amazon for lost or damaged inventory.',
      benefits: [
        'Automatic case detection',
        'Lost inventory claims',
        'Damaged goods claims',
        'Overcharge detection',
        'Case tracking and management'
      ]
    },
    'amazon-ads': {
      title: 'Amazon Ads Management',
      description: 'Manage your advertising campaigns with AI-powered insights and recommendations.',
      benefits: [
        'Campaign performance tracking',
        'ACOS and ROAS monitoring',
        'AI optimization suggestions',
        'Budget management',
        'Keyword performance'
      ]
    },
    'unlimited-chat': {
      title: 'Unlimited AI Chat',
      description: 'Get unlimited access to our AI assistant for all your business questions.',
      benefits: [
        'Unlimited messages per month',
        'Web search integration',
        'Business context awareness',
        'Product recommendations',
        'Market insights'
      ]
    },
    'unlimited-products': {
      title: 'Unlimited Products',
      description: 'Manage unlimited products without restrictions.',
      benefits: [
        'No product limit',
        'Full catalog management',
        'Bulk operations',
        'Advanced filtering',
        'Complete history'
      ]
    }
  };

  const featureInfo = proFeatures[feature] || {
    title: 'Pro Feature',
    description: 'This feature is only available for Pro users.',
    benefits: ['Unlock all Pro features', 'Priority support', 'Advanced analytics']
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 to-blue-50 flex items-center justify-center p-4">
      <div className="max-w-2xl w-full bg-white rounded-2xl shadow-2xl overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-purple-600 to-blue-600 p-8 text-white text-center">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-white bg-opacity-20 rounded-full mb-4">
            <FaCrown className="text-4xl text-yellow-300" />
          </div>
          <h2 className="text-3xl font-bold mb-2">Upgrade to Pro</h2>
          <p className="text-purple-100">
            Unlock {featureInfo.title} and all Pro features
          </p>
        </div>

        {/* Content */}
        <div className="p-8">
          {/* Feature Description */}
          <div className="mb-8">
            <h3 className="text-2xl font-bold text-gray-900 mb-3">
              {featureInfo.title}
            </h3>
            <p className="text-lg text-gray-600 mb-6">
              {featureInfo.description}
            </p>

            {/* Benefits */}
            <div className="space-y-3">
              {featureInfo.benefits.map((benefit, index) => (
                <div key={index} className="flex items-start">
                  <svg className="w-6 h-6 text-green-500 mr-3 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <span className="text-gray-700">{benefit}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Pricing Comparison */}
          <div className="bg-gradient-to-br from-purple-50 to-blue-50 rounded-xl p-6 mb-8">
            <div className="grid md:grid-cols-2 gap-6">
              {/* Current Plan */}
              <div className="bg-white rounded-lg p-4 border-2 border-gray-200">
                <div className="text-sm font-medium text-gray-500 mb-1">Current Plan</div>
                <div className="text-2xl font-bold text-gray-900 mb-2">Starter</div>
                <div className="text-3xl font-bold text-gray-900 mb-1">$49<span className="text-lg text-gray-500">/mo</span></div>
                <ul className="text-sm text-gray-600 space-y-1 mt-4">
                  <li>• 100 products max</li>
                  <li>• 100 chat messages/mo</li>
                  <li>• 90 days order history</li>
                  <li>• Sync every 12 hours</li>
                </ul>
              </div>

              {/* Pro Plan */}
              <div className="bg-gradient-to-br from-purple-600 to-blue-600 rounded-lg p-4 border-2 border-purple-600 relative">
                <div className="absolute -top-3 -right-3 bg-yellow-400 text-purple-900 text-xs font-bold px-3 py-1 rounded-full">
                  RECOMMENDED
                </div>
                <div className="text-sm font-medium text-purple-100 mb-1">Upgrade to</div>
                <div className="text-2xl font-bold text-white mb-2">Pro</div>
                <div className="text-3xl font-bold text-white mb-1">$129<span className="text-lg text-purple-200">/mo</span></div>
                <ul className="text-sm text-purple-100 space-y-1 mt-4">
                  <li>• Unlimited products</li>
                  <li>• Unlimited chat</li>
                  <li>• Complete order history</li>
                  <li>• Sync every 6 hours</li>
                  <li>• All Pro features</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex flex-col sm:flex-row gap-4">
            <button
              onClick={() => navigate('/subscription')}
              className="flex-1 px-8 py-4 bg-gradient-to-r from-purple-600 to-blue-600 text-white font-bold rounded-lg hover:from-purple-700 hover:to-blue-700 transition-all duration-200 shadow-lg hover:shadow-xl flex items-center justify-center"
            >
              Upgrade to Pro
              <FaArrowRight className="ml-2" />
            </button>
            <button
              onClick={() => navigate('/dashboard')}
              className="flex-1 px-8 py-4 bg-gray-100 text-gray-700 font-semibold rounded-lg hover:bg-gray-200 transition-all duration-200"
            >
              Maybe Later
            </button>
          </div>

          {/* Money Back Guarantee */}
          <div className="mt-6 text-center">
            <p className="text-sm text-gray-500">
              14-day free trial • Cancel anytime • No credit card required
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UpgradePrompt;
